import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/** Runs once per browser: old installs had English as default in `lang`; switch them to Arabic. Preserves explicit `ur` / `ar` choices. */
const I18N_AR_DEFAULT_MIGRATION_KEY = 'i18n_default_ar_migrated_v1';

function applyLegacyArabicDefaultMigration() {
  try {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(I18N_AR_DEFAULT_MIGRATION_KEY)) return;

    const saved = localStorage.getItem('lang');
    if (!saved || saved === 'en') {
      localStorage.setItem('lang', 'ar');
    }
    localStorage.setItem(I18N_AR_DEFAULT_MIGRATION_KEY, '1');
  } catch {
    // private mode / quota
  }
}

applyLegacyArabicDefaultMigration();

// Get saved language or default to Arabic
const getInitialLanguage = () => {
  const savedLang = localStorage.getItem('lang');
  if (savedLang && ['en', 'ar', 'ur'].includes(savedLang)) {
    return savedLang;
  }
  return 'ar';
};

// Initialize i18next
i18n
  .use(LanguageDetector) // Language detector
  .use(initReactI18next) // Init with react-i18next
  .init({
    lng: getInitialLanguage(), // Set initial language
    fallbackLng: 'en', // Default language if no translation is found
    supportedLngs: ['en', 'ar', 'ur'], // Supported languages
    interpolation: { escapeValue: false }, // Avoid XSS in translations
    resources: {}, // Initialize resources as an empty object
    react: {
      useSuspense: false, // Disable suspense to prevent issues during loading
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'lang',
    },
    // Return empty string instead of key if translation is missing (temporary until resources load)
    returnEmptyString: false,
    returnNull: false,
    // Use fallback language if translation is missing
    returnObjects: false,
    // Don't show missing keys in production
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lng, ns, key) => {
      // In development, log missing keys
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation key: ${key} for language: ${lng}`);
      }
    },
  });

// Track if resources are loaded
let resourcesLoaded = false;
let resourcesLoading = false;

// Dynamically load translation files
const loadResources = async () => {
  if (resourcesLoading) return; // Prevent multiple simultaneous loads
  if (resourcesLoaded) return; // Already loaded
  
  resourcesLoading = true;
  
  try {
    // Fetch English, Arabic, and Urdu translation files
    const [enRes, arRes, urRes] = await Promise.all([
      fetch('/locales/en/translation.json').then(res => {
        if (!res.ok) throw new Error('Failed to load English translations');
        return res.json();
      }),
      fetch('/locales/ar/translation.json').then(res => {
        if (!res.ok) throw new Error('Failed to load Arabic translations');
        return res.json();
      }),
      fetch('/locales/ur/translation.json').then(res => {
        if (!res.ok) throw new Error('Failed to load Urdu translations');
        return res.json();
      }),
    ]);

    // Add resources to i18n
    i18n.addResources('en', 'translation', enRes);
    i18n.addResources('ar', 'translation', arRes);
    i18n.addResources('ur', 'translation', urRes);

    resourcesLoaded = true;

    // Ensure language is set correctly after resources are loaded
    const currentLang = getInitialLanguage();
    
    // Change language to trigger re-render with loaded resources
    i18n.changeLanguage(currentLang).then(() => {
      // Set document direction and language after language change
      const dir = currentLang === 'ar' || currentLang === 'ur' ? 'rtl' : 'ltr';
      document.documentElement.dir = dir;
      document.documentElement.lang = currentLang;
    });
  } catch (error) {
    console.error("Error loading translation resources", error);
    i18n.changeLanguage('ar');
    document.documentElement.dir = 'rtl';
    document.documentElement.lang = 'ar';
  } finally {
    resourcesLoading = false;
  }
};

// Load resources immediately
loadResources();

// Export function to reload resources if needed
export const reloadResources = () => {
  resourcesLoaded = false;
  loadResources();
};

export default i18n;
